<template>
  <AutoComplete
    ref="autoCompleteRef"
    :model-value="input"
    :input-id="id"
    :disabled="disabled"
    :loading="products.loading"
    :placeholder="t('lineItems.productOrDescription')"
    :suggestions="products.data"
    :dropdown="!disabled"
    :show-clear="!disabled"
    class="product-quick-search"
    overlay-class="product-quick-search-overlay"
    dropdown-mode="current"
    option-label="description"
    option-value="id"
    @clear="onClear"
    @complete="onProductSearch"
    @item-select="onItemSelected($event.value)"
    @blur="onAutoCompleteBlur"
    @update:model-value="onItemSelected"
    @value-change="onValueChange"
  />
</template>

<script lang="ts" setup>
import { useResizeObserver } from '@vueuse/core';
import { CancelTokenSource, default as axios } from 'axios';
import isEqual from 'lodash/isEqual';
import type { Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast, useUid } from '@rygen/rygen-ui/composables';
import {
  DensityUom,
  DimsUom,
  HazmatData,
  Loadable,
  Product,
  TemperatureSettings,
  WeightUom,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { LineItem, ProductSearchPayload } from '@/model';

import AutoComplete from 'primevue/autocomplete';

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('product-quick-search').uid,
  },
  modelValue: {
    type: LineItem,
    default: () => new LineItem(),
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'clear'): void;
  (event: 'update:model-value', newVal: LineItem): void;
}>();

const { t } = useI18n();
const toast = useToast();

const autoCompleteRef: Ref<typeof AutoComplete | null> = ref(null);
const autoCompleteWidth: Ref<number> = ref(500);
useResizeObserver(autoCompleteRef as any, ([entry]) => {
  const { width } = entry.contentRect;
  autoCompleteWidth.value = width;
  document.documentElement.style.setProperty(
    '--autocomplete-overlay-max-width',
    `${width}px`,
  );
});

const Clazz = computed(() => LineItem.getSubclass(modelValue.value));
const internalValue: Ref<LineItem> = ref(new Clazz.value(modelValue.value));
const input = ref(internalValue.value.description ?? '');
/** The current product search results */
const products: Ref<Loadable<Product[]>> = ref({ loading: false });
const cancelRequest: Ref<CancelTokenSource | undefined> = ref(undefined);

const onProductSearch = async () => {
  cancelRequest.value?.cancel();
  cancelRequest.value = axios.CancelToken.source();

  try {
    products.value.loading = true;

    const payload = new ProductSearchPayload({
      criteria: internalValue.value.description,
    });
    const result = await api.product.search(payload, cancelRequest.value);
    products.value.data = result.content;

    // ensure query available as a suggestion
    if (internalValue.value.description) {
      const query = internalValue.value.description;
      const queryExists = products.value.data.some(
        (product) => product.description === query,
      );
      if (!queryExists) {
        products.value.data.unshift(
          new Clazz.value({ id: null, description: query }),
        );
      }
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    products.value.error = e;
    console.error('Error loading products', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('products.products', 2),
      }),
      error: e,
    });
  } finally {
    products.value.loading = false;
  }
};

const onItemSelected = async (query: string | Product) => {
  if (typeof query === 'string') {
    internalValue.value.description = query;
    return;
  }

  const product = query;
  internalValue.value.description = product.description;
  internalValue.value.productId = product.id;
  internalValue.value.productNumber = product.productNumber;
  internalValue.value.handlingUnit = product.handlingUnit;
  internalValue.value.packagingUnit = product.packagingUnit;
  internalValue.value.packagingQty = product.packagingQty;
  internalValue.value.freightClass = product.freightClass;
  internalValue.value.quantity = product.quantity;
  internalValue.value.lengthDim = product.lengthDim;
  internalValue.value.widthDim = product.widthDim;
  internalValue.value.heightDim = product.heightDim;
  internalValue.value.dimensionalUom = product.dimensionalUom ?? DimsUom.INCH;
  internalValue.value.density = product.density;
  internalValue.value.densityUom =
    product.densityUom ?? DensityUom.POUND_PER_CUBIC_FOOT;
  internalValue.value.weight = product.weight;
  internalValue.value.weightUom = product.weightUom ?? WeightUom.POUND;
  internalValue.value.nmfcNumber = product.nmfcNumber;
  internalValue.value.turnable = product.turnable;
  internalValue.value.stackable = product.stackable;

  if (product.hazmatData?.hazmat) {
    internalValue.value.hazmatData.hazmat = product.hazmatData.hazmat;
    internalValue.value.hazmatData.unNumber = product.hazmatData?.unNumber;
    internalValue.value.hazmatData.hazardDescription =
      product.hazmatData?.hazardDescription;
    internalValue.value.hazmatData.hazardClass =
      product.hazmatData?.hazardClass;
    internalValue.value.hazmatData.packingGroup =
      product.hazmatData?.packingGroup;
  } else {
    internalValue.value.hazmatData = new HazmatData();
  }
  if (product.temperatureSettings?.needsRefrigeration) {
    internalValue.value.temperatureSettings.needsRefrigeration =
      product.temperatureSettings.needsRefrigeration;
    internalValue.value.temperatureSettings.minTemp =
      product.temperatureSettings.minTemp;
    internalValue.value.temperatureSettings.maxTemp =
      product.temperatureSettings.maxTemp;
    internalValue.value.temperatureSettings.temperatureUom =
      product.temperatureSettings.temperatureUom;
  } else {
    internalValue.value.temperatureSettings = new TemperatureSettings();
  }
};

const onClear = () => {
  if (internalValue.value.productId) {
    emit('clear');
  } else {
    internalValue.value.description = undefined;
  }
};

/**
 * Hack to get around `AutoComplete` not emitting it's `clear` event
 *
 * This function checks if the value is nullish and emits a `clear` event,
 * otherwise it ignores the change.
 */
const onValueChange = (newVal: string) => {
  if (!newVal) {
    onClear();
  }
};

const onAutoCompleteBlur = () => {
  // cancel ongoing http request
  cancelRequest.value?.cancel();

  // Cancel 'complete' callback in autocomplete component
  // This handles the case where the user enters or pastes a value and then
  // quickly clicks or tabs away.
  // Without clearing the timeout, the 'complete' callback will be called
  // which will invoke the search which will then open the dropdown, even though
  // the user is no longer on this component at all.
  // This is totally abusing the internal API of the autocomplete component, I
  // fully expect it to break in the future.
  const searchTimeout = (autoCompleteRef.value as any)?.searchTimeout;
  clearTimeout(searchTimeout);
};

watch(
  modelValue,
  (newVal: LineItem) => {
    internalValue.value = new Clazz.value(newVal);
    input.value = newVal.description ?? '';
  },
  { deep: true, immediate: true },
);

watch(
  internalValue,
  (newVal: LineItem) => {
    if (!isEqual(modelValue.value, newVal)) {
      emit('update:model-value', newVal);
      input.value = newVal.description ?? '';
    }
  },
  { deep: true },
);
</script>

<style>
.product-quick-search {
  .p-autocomplete-input {
    width: 100%;
  }
}

.product-quick-search-overlay.p-autocomplete-overlay {
  max-width: var(--autocomplete-overlay-max-width, 500px);

  .p-autocomplete-option {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}
</style>
